import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { forgotPasswordSchema, resetPasswordSchema } from '@app/shared';
import { APP_NAME } from '../../app/theme';
import { useBrandLook } from '../../app/BrandTheme';
import { AppLockup } from '../../components/BrandLogo';
import { useFeatureReleased } from '../../release';
import { ApiError, api, errorMessage } from '../../lib/api';

/** Field problems of a zod parse, first message per field. */
function problemsOf(
  issues: Array<{ path: PropertyKey[]; message: string }>,
): Record<string, string> {
  const found: Record<string, string> = {};
  for (const issue of issues) found[String(issue.path[0])] ??= issue.message;
  return found;
}

/**
 * "Forgot your password?" (release switch `forgot-password`): the user name is sent, and the
 * answer is the same whether or not it exists.
 */
export function ForgotPasswordDialog({ onClose }: { onClose: () => void }) {
  const [loginId, setLoginId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ loginId });
    if (!parsed.success) {
      setProblem(parsed.error.issues[0]?.message ?? 'User name is required');
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const answer = await api.post<{ message: string }>('/auth/forgot-password', parsed.data);
      setSent(answer.message);
    } catch (err) {
      setFailure(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => void submit(e)} noValidate aria-label="Forgot your password?">
        <DialogTitle>Forgot your password?</DialogTitle>
        <DialogContent>
          {sent ? (
            <Alert severity="success">{sent}</Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Enter your user name. A link to choose a new password is sent to the e-mail address
                of your user.
              </DialogContentText>
              {failure ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {failure}
                </Alert>
              ) : null}
              <TextField
                label="User Name"
                autoComplete="username"
                autoFocus
                fullWidth
                value={loginId}
                onChange={(e) => {
                  setLoginId(e.target.value);
                  setProblem(null);
                }}
                error={Boolean(problem)}
                helperText={problem}
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{sent ? 'Close' : 'Cancel'}</Button>
          {sent ? null : (
            <Button type="submit" variant="contained" disabled={busy}>
              Send Link
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** The page the e-mailed link opens: the new password twice, then back to Log In. */
export function ResetPasswordPage() {
  const featureOn = useFeatureReleased();
  const brandLook = useBrandLook();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = { token, newPassword, confirmPassword };
    const parsed = resetPasswordSchema.safeParse(body);
    if (!parsed.success) {
      const found = problemsOf(parsed.error.issues);
      if (found.token) setFailure(found.token);
      setProblems(found);
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      await api.post('/auth/reset-password', body);
      setDone(true);
    } catch (err) {
      const details = err instanceof ApiError ? err.details : [];
      const tokenProblem = details.find((d) => d.path === 'token');
      setFailure(tokenProblem?.message ?? errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
          {brandLook ? (
            <AppLockup height={72} />
          ) : featureOn('brand-logo') ? (
            <AppLockup height={72} />
          ) : (
            APP_NAME
          )}
        </Typography>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Choose a new password
        </Typography>
        {done ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your password has been changed. Log in with the new one.
            </Alert>
            <MuiLink component={Link} to="/login">
              Log In
            </MuiLink>
          </>
        ) : (
          <form onSubmit={(e) => void submit(e)} noValidate aria-label="Choose a new password">
            {failure ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {failure}
              </Alert>
            ) : null}
            <TextField
              label="New Password"
              type="password"
              autoComplete="new-password"
              autoFocus
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={Boolean(problems.newPassword)}
              helperText={
                problems.newPassword ?? 'At least 8 characters, with a letter and a digit'
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={Boolean(problems.confirmPassword)}
              helperText={problems.confirmPassword}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={busy}>
              Save
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <MuiLink component={Link} to="/login">
                Back to Log In
              </MuiLink>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}
